import { v4 } from 'uuid';
import { EntryKinds } from '@i-ark/models';
import { extractBoundingBoxFromPolygonVertices } from '@i-ark/utils';
import { transformPlacementToVertices } from '../transformPlacementToVertices.js';

const transformPageEntryV1ToV2 = (page, pageEntry, index) => {
  var _a;
  return {
    id: index.toString(),
    items: {
      ...transformInputEntries(page, pageEntry, index),
      ...transformShapeColorings(page, pageEntry, index)
    },
    clones: [
      ...transformInputClones(page, pageEntry, index),
      ...transformShapeClones(page, pageEntry, index)
    ],
    drawings: ((_a = pageEntry.drawing) == null ? void 0 : _a.filter(isDrawingDefined).map(
      (drawing, drawingIndex) => transformDrawing(index, drawing, drawingIndex)
    )) ?? [],
    lines: []
  };
};
function transformInputEntries(page, pageEntry, pageIndex) {
  var _a;
  const result = {};
  (_a = pageEntry.inputs) == null ? void 0 : _a.filter(isInputEntryDefined).forEach((entry, index) => {
    var _a2, _b;
    result[`${pageIndex.toString()}_item-${index.toString()}`] = isMovedInputEntry(entry) ? {
      position: {
        x: entry.origin.x / (((_a2 = page.naturalSize) == null ? void 0 : _a2.width) ?? 1),
        y: entry.origin.y / (((_b = page.naturalSize) == null ? void 0 : _b.height) ?? 1)
      },
      zIndex: entry.weight,
      entry: {
        kind: EntryKinds.INPUT,
        value: entry.answer
      }
    } : {
      entry: {
        kind: EntryKinds.INPUT,
        value: entry.answer
      }
    };
  });
  return result;
}
function transformInputClones(page, pageEntry, pageIndex) {
  var _a;
  return ((_a = pageEntry.clones) == null ? void 0 : _a.filter(isInputCloneDefined).map((clone) => {
    var _a2, _b;
    return {
      itemId: `${pageIndex.toString()}_item-${clone.cloneOf.toString()}`,
      cloneId: v4(),
      position: {
        x: clone.origin.x / (((_a2 = page.naturalSize) == null ? void 0 : _a2.width) ?? 1),
        y: clone.origin.y / (((_b = page.naturalSize) == null ? void 0 : _b.height) ?? 1)
      },
      zIndex: clone.weight,
      entry: {
        kind: EntryKinds.INPUT,
        value: clone.answer
      }
    };
  })) ?? [];
}
function transformShapeColorings(page, pageEntry, pageIndex) {
  if (pageEntry.shape_drawing === void 0) {
    return {};
  }
  const result = {};
  const shapeColorings = Array.isArray(pageEntry.shape_drawing) ? { ...pageEntry.shape_drawing } : pageEntry.shape_drawing;
  Object.keys(shapeColorings ?? {}).forEach((shapeId) => {
    const shape = shapeColorings[shapeId];
    if (!shape) {
      return;
    }
    const v2ShapeId = `${pageIndex.toString()}_legacyCanvasShape-${shapeId}`;
    const position = extractPositionFromShape(page, v2ShapeId, shape);
    result[v2ShapeId] = isMovedShapeColoring(shape) && position !== void 0 ? {
      position: {
        x: position.x,
        y: position.y
      },
      zIndex: shape.weight,
      entry: {
        kind: EntryKinds.LEGACY_CANVAS_SHAPE,
        backgroundColor: shape.background
      }
    } : {
      entry: {
        kind: EntryKinds.LEGACY_CANVAS_SHAPE,
        backgroundColor: shape.background
      }
    };
  });
  return result;
}
function transformShapeClones(page, pageEntry, pageIndex) {
  const clones = pageEntry.shape_drawing_clones;
  if (clones === void 0) {
    return [];
  }
  return Object.keys(pageEntry.shape_drawing_clones ?? {}).flatMap((shapeId) => {
    var _a;
    return (_a = clones[String(shapeId)]) == null ? void 0 : _a.map((shape) => {
      const v2ShapeId = `${pageIndex.toString()}_legacyCanvasShape-${shapeId}`;
      const position = extractPositionFromShape(page, v2ShapeId, shape);
      return {
        itemId: v2ShapeId,
        cloneId: v4(),
        position: {
          x: (position == null ? void 0 : position.x) ?? 0,
          y: (position == null ? void 0 : position.y) ?? 0
        },
        zIndex: shape.weight,
        entry: {
          kind: EntryKinds.LEGACY_CANVAS_SHAPE,
          backgroundColor: shape.background
        }
      };
    });
  }).filter((clone) => !!clone);
}
function transformDrawing(pageIndex, drawing, index) {
  const minCoords = {
    x: Math.min(...drawing.items.map(([x]) => x)),
    y: Math.min(...drawing.items.map(([, y]) => y))
  };
  const maxCoords = {
    x: Math.max(...drawing.items.map(([x]) => x)),
    y: Math.max(...drawing.items.map(([, y]) => y))
  };
  return {
    id: `${pageIndex.toString()}_drawing-${index.toString()}`,
    position: {
      x: minCoords.x / 100,
      y: minCoords.y / 100
    },
    size: {
      width: (maxCoords.x - minCoords.x) / 100,
      height: (maxCoords.y - minCoords.y) / 100
    },
    points: drawing.items.map(([x, y]) => ({
      x: (x - minCoords.x) / (maxCoords.x - minCoords.x),
      y: (y - minCoords.y) / (maxCoords.y - minCoords.y)
    })),
    style: {
      lineWidth: drawing.size * 0.8,
      color: drawing.color
    }
  };
}
function extractPositionFromShape(page, shapeId, clonedDrawing) {
  const item = page.items.find((it) => it.id === shapeId);
  if (clonedDrawing.placement === void 0) {
    return void 0;
  }
  switch (item == null ? void 0 : item.type) {
    case "polygon": {
      const vertices = transformPlacementToVertices(
        clonedDrawing.placement
      );
      const boundingBox = extractBoundingBoxFromPolygonVertices(vertices);
      return {
        x: boundingBox.left,
        y: boundingBox.top
      };
    }
    case "circle": {
      const [originX, originY] = clonedDrawing.placement;
      return {
        x: originX / 100 - ((item == null ? void 0 : item.size.width) ?? 0) / 2,
        y: originY / 100 - ((item == null ? void 0 : item.size.width) ?? 0) / 2 / page.aspectRatio
      };
    }
    case "rectangle": {
      const [x, y] = clonedDrawing.placement;
      return {
        x: x / 100,
        y: y / 100
      };
    }
    default:
      return {
        x: 0,
        y: 0
      };
  }
}
function isInputEntryDefined(entry) {
  return entry !== void 0 && entry !== null;
}
function isInputCloneDefined(clone) {
  return clone !== void 0 && clone !== null;
}
function isDrawingDefined(entry) {
  return entry !== void 0 && entry !== null;
}
function isMovedInputEntry(entry) {
  return entry.weight !== null && "origin" in entry;
}
function isMovedShapeColoring(entry) {
  return entry.weight !== null;
}

export { transformPageEntryV1ToV2 };
//# sourceMappingURL=transformPageEntryV1ToV2.js.map
